import {
	MULTI_VERSION_CF_TYPE,
	VERSION_CF_TYPE,
	AFFECTS_VERSIONS_TYPE,
	FIX_VERSIONS_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { DataTransformFn } from '../../../bulk-edit-transform/types.tsx';
import { transformOptionsUsingMultiAction } from '../../../bulk-edit-transform/utils.tsx';
import type {
	jiraBusinessMultipleVersionFieldFragment_selectedVersionsConnection_edges_node as jiraVersion,
	jiraBusinessMultipleVersionFieldFragment as jiraMultipleVersionPickerField,
} from '../fragment/__generated_apollo__/jiraBusinessMultipleVersionFieldFragment.ts';
import type { jiraBusinessSingleVersionFieldFragment as jiraSingleVersionPickerField } from '../fragment/__generated_apollo__/jiraBusinessSingleVersionFieldFragment.ts';
import type { MultiVersionFormData, SingleVersionFormData } from './types.tsx';

export const transformSingleVersionFieldForOptimisticUpdates: DataTransformFn<
	jiraSingleVersionPickerField,
	SingleVersionFormData
> = (
	existingField: jiraSingleVersionPickerField,
	fieldData: SingleVersionFormData,
): jiraSingleVersionPickerField => ({
	...existingField,
	version: fieldData
		? {
				__typename: 'JiraVersion',
				versionId: fieldData.value,
				name: fieldData.label,
			}
		: null,
});

export const transformMultiVersionFieldForOptimisticUpdates: DataTransformFn<
	jiraMultipleVersionPickerField,
	MultiVersionFormData
> = (
	existingField: jiraMultipleVersionPickerField,
	fieldData: MultiVersionFormData,
): jiraMultipleVersionPickerField => {
	const action = fieldData.action;
	const existingVersions =
		existingField.selectedVersionsConnection?.edges?.map((edge) => edge?.node).filter(Boolean) ??
		[];
	const versionNodes = transformOptionsUsingMultiAction<jiraVersion>({
		existingOptions: existingVersions,
		options: fieldData.value.map((value) => ({
			__typename: 'JiraVersion',
			versionId: value.value,
			name: value.label,
		})),
		action,
		isEqual: (option: jiraVersion, existingOption: jiraVersion) =>
			option.versionId === existingOption.versionId,
	});
	return {
		...existingField,
		selectedVersionsConnection: {
			__typename: 'JiraVersionConnection',
			edges:
				versionNodes?.map((node) => ({
					__typename: 'JiraVersionEdge',
					node,
				})) ?? null,
		},
	};
};

export const versionOptimisticUpdatesHandler: {
	[key: string]:
		| DataTransformFn<jiraSingleVersionPickerField, SingleVersionFormData>
		| DataTransformFn<jiraMultipleVersionPickerField, MultiVersionFormData>;
} = {
	[VERSION_CF_TYPE]: transformSingleVersionFieldForOptimisticUpdates,
	[MULTI_VERSION_CF_TYPE]: transformMultiVersionFieldForOptimisticUpdates,
	[FIX_VERSIONS_TYPE]: transformMultiVersionFieldForOptimisticUpdates,
	[AFFECTS_VERSIONS_TYPE]: transformMultiVersionFieldForOptimisticUpdates,
};
